import {WidgetFullEditorHostContext} from "../common/WidgetFullEditorPortal"
import React, {CSSProperties, FC, useContext, useEffect, useState} from "react"
import {cancelEditWidget, TextWidgetData, useAppDispatch} from "@dv5/domain"
import {messagesService} from "@dv5/api"
import {isModPressed} from "@dv5/components"

import {WidgetPlainText} from "../common/WidgetPlainText"

interface Props {
    contentStyle: CSSProperties
    canvasId: string
    widgetId: string
    data: TextWidgetData
    text: string
    containerRef: React.RefObject<HTMLDivElement | null>
}

export const TextWidgetInlineEditor: FC<Props> = ({
    canvasId,
    text,
    widgetId,
    data,
    contentStyle,
    containerRef,
}) => {
    const [editingText, setEditingText] = useState(text)
    const fullEditorHost = useContext(WidgetFullEditorHostContext)
    const dispatch = useAppDispatch()
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (containerRef.current && !containerRef.current.contains(target) && !fullEditorHost?.contains(target)) {
                if (editingText !== text) {
                    saveText(editingText)
                } else {
                    onCancelEdit()
                }
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [text, editingText, fullEditorHost])

    const onCancelEdit = () => {
        dispatch(cancelEditWidget({
            canvasId: canvasId,
            changes: widgetId,
        }))
    }

    const saveText = (newText: string) => {
        messagesService.send('canvas.change-widget-data', {
            canvasId: canvasId,
            widgetId: widgetId,
            patchedPaths: ['text'],
            data: {
                ...data,
                text: newText,
            }
        })
    }

    useEffect(() => {
        setEditingText(text)
    }, [text])

    const changeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const keyHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && isModPressed(event)) {
            event.preventDefault()
            event.stopPropagation()
            saveText(editingText)
        } else if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            onCancelEdit()
        }
    }

    return (
        <WidgetPlainText
            text={editingText}
            style={contentStyle}
            editorProps={{
                onChange: changeText,
                onKeyDown: keyHandler,
                autoFocus: true,
                'aria-label': 'Widget text',
            }}
        />
    )
}
